import type { Analisis, Mapeo } from '../api/importacion'

/**
 * «Así quedarán»: los primeros productos de la hoja tal y como los va a crear el importador,
 * para que el cliente los reconozca antes de pulsar (nombre, precio, categorías y fotos). Es
 * una vista y nada más: lo que se importa lo decide el núcleo, y lo vuelve a leer entero.
 */

const ENTIDADES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', '#39': "'", apos: "'", nbsp: ' ' }

/** El texto de una celda con HTML (las descripciones de WooCommerce), sin etiquetas. */
export const textoPlano = (valor: string) =>
  valor
    .replace(/<[^>]*>/g, ' ')
    .replace(/&(amp|lt|gt|quot|#39|apos|nbsp);/g, (_, e: string) => ENTIDADES[e] ?? ' ')
    .replace(/\s+/g, ' ')
    .trim()

/** «2890», «2.890,50», «2,890.50», «S/ 1 290» → número; lo que no es un precio, null. */
export const precioDe = (valor: string): null | number => {
  let t = valor.replace(/[^\d.,-]/g, '')
  if (!/\d/.test(t)) return null
  const coma = t.lastIndexOf(','),
    punto = t.lastIndexOf('.')
  if (coma >= 0 && punto >= 0) t = coma > punto ? t.replace(/\./g, '').replace(',', '.') : t.replace(/,/g, '')
  else if (coma >= 0) t = /,\d{1,2}$/.test(t) ? t.replace(',', '.') : t.replace(/,/g, '')
  const n = Number(t)
  return Number.isFinite(n) ? n : null
}

/*
 * Las categorías se parten igual que en el núcleo (`src/importacion/importar.ts` y
 * `woocommerce.ts`); si allí cambia, aquí también, o la vista previa enseñará otra cosa.
 */

/** Un nivel dentro de otro: «>», «»», «›», «→», «->», o una barra con espacios («Audio/Vídeo» es un nombre). */
const SEPARADOR_DE_NIVEL = /\s*(?:->|>|»|›|→)\s*|\s+\/\s+/

const sinRepetidas = (rutas: string[][]) => [...new Map(rutas.map((r) => [r.join('\u0000').toLowerCase(), r])).values()]

/**
 * «UPS > Monofásico | Ofertas» → [['UPS', 'Monofásico'], ['Ofertas']]. Varias se separan con «|», «;»
 * o un salto de línea, y con «, » solo si la celda ya usa niveles: sin ellos, una coma suele ser del
 * nombre («Hogar, jardín y terraza»). En WooCommerce las separa la coma, y «\,» es una coma del nombre.
 */
export const rutasDeCategorias = (valor: string, woocommerce = false): string[][] => {
  const partes = woocommerce
    ? valor.split(/(?<!\\),/).map((ruta) => ruta.replace(/\\,/g, ','))
    : valor.split(SEPARADOR_DE_NIVEL.test(valor) ? /\s*[|;\n]\s*|,\s+/ : /\s*[|;\n]\s*/)
  return sinRepetidas(
    partes
      .map((ruta) =>
        ruta
          .split(woocommerce ? /\s*>\s*/ : SEPARADOR_DE_NIVEL)
          .map((tramo) => tramo.trim())
          .filter(Boolean),
      )
      .filter((ruta) => ruta.length > 0),
  )
}

/** Las columnas Subcategoría y Sub-subcategoría cuelgan de la primera categoría de la fila; sin categoría, la subcategoría es la principal. */
export const conSubcategorias = (rutas: string[][], niveles: string[][][]): string[][] => {
  const cola = niveles.filter((n) => n.length > 0).flatMap((n) => n[0] ?? [])
  if (cola.length === 0) return rutas
  if (rutas.length === 0) return [cola]
  return sinRepetidas([[...rutas[0]!, ...cola], ...rutas.slice(1)])
}

/** Cuántas imágenes hay en la celda: separadas por «|», «;», saltos o «, » delante de otra dirección. */
export const cuantasImagenes = (valor: string) => valor.split(/\s*[|;\n]\s*|,\s*(?=https?:\/\/)/i).filter((x) => x.trim() !== '').length

export type ProductoDeMuestra = {
  nombre: string
  precio: null | number
  /** El precio tachado: el anterior, o el normal cuando hay una oferta más baja. */
  antes: null | number
  categorias: string[][]
  fotos: number
  sku: null | string
}

/** Las primeras filas de la muestra como productos: sin las variaciones de WooCommerce, que van con su padre. */
export const productosDeMuestra = (analisis: Analisis, mapeo: Mapeo[], cuantos = 3): ProductoDeMuestra[] => {
  const columna = (campo: string) => mapeo.find((m) => m.campo === campo)?.indice ?? -1
  const [iNombre, iPrecio, iOferta, iAntes, iCategorias, iSub, iSub2, iImagenes, iSku] = [
    'nombre',
    'precio',
    'precioOferta',
    'precioAntes',
    'categorias',
    'subcategoria',
    'subcategoria2',
    'imagenes',
    'sku',
  ].map(columna)
  if (iNombre === undefined || iNombre < 0) return []
  const woocommerce = analisis.formato === 'woocommerce'
  const iTipo = analisis.cabeceras.findIndex((c) => /^(tipo|type)$/i.test(c.trim()))
  const celda = (fila: string[], i: number | undefined) => (i === undefined || i < 0 ? '' : (fila[i] ?? '').trim())
  return analisis.muestra
    .filter((fila) => celda(fila, iNombre) !== '' && !/variation/i.test(celda(fila, iTipo)))
    .slice(0, cuantos)
    .map((fila) => {
      const normal = precioDe(celda(fila, iPrecio))
      const oferta = precioDe(celda(fila, iOferta))
      const anterior = precioDe(celda(fila, iAntes))
      const conOferta = oferta !== null && normal !== null && oferta < normal
      return {
        nombre: textoPlano(celda(fila, iNombre)),
        precio: conOferta ? oferta : normal,
        antes: conOferta ? normal : anterior !== null && normal !== null && anterior > normal ? anterior : null,
        categorias: conSubcategorias(rutasDeCategorias(celda(fila, iCategorias), woocommerce), [rutasDeCategorias(celda(fila, iSub)), rutasDeCategorias(celda(fila, iSub2))]),
        fotos: cuantasImagenes(celda(fila, iImagenes)),
        sku: celda(fila, iSku) || null,
      }
    })
}

/** Un precio con dos decimales, sin símbolo: la moneda es la de la web y la pone el núcleo al importar. */
export const precioEnTexto = (n: number) =>
  // `useGrouping: 'always'`: en español un número de cuatro cifras sale sin separador («2890») y el de cinco con él («12.900»).
  new Intl.NumberFormat('es', { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: 'always' } as Intl.NumberFormatOptions).format(n)
